import {
  customGoal,
  ExperimentConfigWithDefault,
  goalWithValue,
  stage,
  trackWithDefaultStage
} from "@flights/et-universal";
import etMicroConversion from "../../utils/et-microconversion";

/**
 *NB: If we ever move this file to a package, make the experiment-utils-imports rule work with the new location.
 */

import { isOfMetaOrigin } from "../marketing-url-params";
import { debugTrackingNames } from "@flights/et-universal";
import { trackMetaCustomGoal, trackMetaExperimentStage } from "../meta-et-tracking";

type UtilMapForGoalsWithValue<T extends string> = Record<T, (value: number) => void>;
type TrackingFn<T extends string> = (name: T, value: number) => void;

export function createTrackingUtils<TStage extends string, TGoal extends string, TGoalValue extends string>(
  config: ExperimentConfigWithDefault<TStage, TGoal, TGoalValue>
) {
  debugTrackingNames.set(config);

  return {
    name: () => config.name,
    trackWithDefaultStage: () => trackWithDefaultStage(config.name, config.defaultStage),
    stages: createNumericTrackingUtils(stage, config.name, config.stages),
    goals: createNumericTrackingUtils(customGoal, config.name, config.goals),
    goalsWithValue: createCustomGoalWithValueTrackingUtilsV2(config.goalsWithValue)
  };
}

function createCustomGoalWithValueTrackingUtilsV2<T extends string>(goals: T[] = []): UtilMapForGoalsWithValue<T> {
  const utilMap = {} as UtilMapForGoalsWithValue<T>;

  goals.forEach((goal) => {
    utilMap[goal] = (value: number) => goalWithValue(goal, value);
  });

  return utilMap;
}

function createMetaCustomGoalWithValueTrackingUtils<T extends string>(goals: T[] = []): UtilMapForGoalsWithValue<T> {
  const utilMap = {} as UtilMapForGoalsWithValue<T>;

  goals.forEach((goal) => {
    utilMap[goal] = (value: number) => isOfMetaOrigin() && goalWithValue(goal, value);
  });

  return utilMap;
}

function createNumericTrackingUtils<TName extends string, TKey extends string>(
  trackingFn: TrackingFn<TName>,
  expName: TName,
  indexMap: Record<TKey, number>
) {
  const utilMap = {} as Record<TKey, VoidFunction>;
  type Index = typeof indexMap[keyof typeof indexMap];

  for (const [name, index] of Object.entries(indexMap)) {
    utilMap[name as TKey] = () => trackingFn(expName, index as Index);
  }

  return utilMap;
}

export function createMetaTrackingUtils<TStage extends string, TGoal extends string, TGoalValue extends string>(
  config: ExperimentConfigWithDefault<TStage, TGoal, TGoalValue>
) {
  debugTrackingNames.set(config);

  return {
    name: () => config.name,
    trackWithDefaultStage: () => isOfMetaOrigin() && trackWithDefaultStage(config.name, config.defaultStage),
    stages: createNumericTrackingUtils(trackMetaExperimentStage, config.name, config.stages),
    goals: createNumericTrackingUtils(trackMetaCustomGoal, config.name, config.goals),
    goalsWithValue: createMetaCustomGoalWithValueTrackingUtils(config.goalsWithValue)
  };
}

/**
 * ### Experimental!
 * do not use in your experiments. this is an in-progress experimental way of mesuring microconversion without blackoout.
 *
 * More info: [et-microconversion.md](../../utils/et-microconversion.md)
 */
export function createMicroConversionTrackingUtils<
  TStage extends string,
  TGoal extends string,
  TGoalValue extends string
>(config: ExperimentConfigWithDefault<TStage, TGoal, TGoalValue> & { mainStage: number }) {
  debugTrackingNames.set(config);
  return {
    name: () => config.name,
    trackWithDefaultStage: () => etMicroConversion.trackWithDefaultStage(config.name, config.defaultStage),
    stages: createNumericTrackingUtils(
      (hashedTagname: string, id: number) => {
        etMicroConversion.stage(hashedTagname, id, id === config.mainStage);
      },
      config.name,
      config.stages
    ),
    goals: createNumericTrackingUtils(etMicroConversion.customGoal, config.name, config.goals),
    goalsWithValue: createCustomGoalWithValueTrackingUtilsV2(config.goalsWithValue)
  };
}
